import * as fabricTypes from 'fabric'
import { fabric } from '~/utils/editor/fabric'

type SideControl = 'mr' | 'mt' | 'ml' | 'mb'

/**
 * Sets fabric controls
 */
export const setControls = (object: fabricTypes.FabricObject, sideControls: boolean = true) => {
  object.set('cornerColor', '#FFFFFF')
  object.set('cornerStrokeColor', '#6610F2')
  object.set('borderColor', '#6610F2')
  object.set('transparentCorners', false)
  object.set('cornerSize', 10)
  object.set('cornerStyle', 'circle')
  object.set('borderOpacityWhenMoving', 1)

  ;([ 'ml', 'mr', 'mt', 'mb' ] as SideControl[]).forEach((side: SideControl) => {
    object.setControlVisible(side, sideControls)
    object.controls[side].render = renderFullSideControl(side)
  })
}

export const setTextboxSideControls = (object: fabricTypes.FabricObject, showSideControls: boolean = true) => {
  object.set('cornerColor', '#FFFFFF')
  object.set('cornerStrokeColor', '#6610F2')
  object.set('borderColor', '#6610F2')
  object.set('transparentCorners', false)
  object.set('cornerSize', 10)
  object.set('cornerStyle', 'circle')
  object.set('borderOpacityWhenMoving', 1)

  if (showSideControls) {
    const renderFunctionCreator = (startAngle: number, endAngle: number, side: 'left' | 'right'): RenderFunction => {
      return (
        ctx: CanvasRenderingContext2D,
        left: number,
        top: number,
        _styleOverride: unknown,
        fabricObject: fabricTypes.FabricObject,
      ) => {
        ctx.save()
        ctx.translate(left, top)
        ctx.rotate(fabric.util.degreesToRadians(fabricObject.angle ?? 0)) 
        ctx.fillStyle = <string>fabricObject.cornerColor
        ctx.strokeStyle = <string>fabricObject.cornerStrokeColor
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.arc(0, 0, 5, startAngle, endAngle, false)
        const lineOffsetX = side === 'left' ? -0.5 : 0.5
        ctx.moveTo(lineOffsetX, -5)
        ctx.lineTo(lineOffsetX, 5)
        ctx.fill()
        ctx.stroke()
        ctx.restore()
      }
    }

    object.controls.ml.render = renderFunctionCreator(0.5 * Math.PI, 1.5 * Math.PI, 'left')
    object.controls.mr.render = renderFunctionCreator(1.5 * Math.PI, 0.5 * Math.PI, 'right')
    object.setControlVisible('ml', true)
    object.setControlVisible('mr', true)
  } else {
    object.setControlVisible('ml', false)
    object.setControlVisible('mr', false)
  }
  object.setControlVisible('mt', false)
  object.setControlVisible('mb', false)
}

type RenderFunction = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  styleOverride: unknown,
  object: fabricTypes.FabricObject,
) => void

// Functions for calculate control width and height
interface ControlSizeGetter {
  height: (object: fabricTypes.FabricObject) => number
  width: (object: fabricTypes.FabricObject) => number
}
// Functions for vertical side controls
const verticalSideControlSize: ControlSizeGetter = {
  height: (object: fabricTypes.FabricObject) => {
    return object.getScaledHeight() * (object.canvas?.getZoom() ?? 1)
  },
  width: () => {
    return 10
  },
}
// Functions for horizontal side controls
const horizontalSideControlSize: ControlSizeGetter = {
  height: () => {
    return 10
  },
  width: (object: fabricTypes.FabricObject) => {
    return object.getScaledWidth() * (object.canvas?.getZoom() ?? 1)
  },
}

/**
 * Generates function for render control for full side length
 * @param side Control side
 * @returns Function for render control
 */
const renderFullSideControl = (side: SideControl): RenderFunction => {
  const sizeGetter = (side === 'mt' || side === 'mb') ?
    horizontalSideControlSize :
    verticalSideControlSize
  return (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    _styleOverride: unknown,
    object: fabricTypes.FabricObject
  ) => {
    ctx.save()
    ctx.translate(x, y)
    ctx.rotate(fabric.util.degreesToRadians(object.angle ?? 0))
    const width = sizeGetter.width(object)
    const height = sizeGetter.height(object)
    object.controls[side].sizeX = width
    object.controls[side].sizeY = height
    ctx.rect(-width / 2, -height / 3, width, height)
    ctx.restore()
  }
}
